import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FolderKanban, AlertTriangle, Clock, DollarSign, Target, Shield,
  Users, ChevronDown, ChevronRight, ChevronUp, Mail, Crown, Briefcase,
  Map, ExternalLink, Calendar, Link2, Flag, FileText, AlertCircle,
  Layers, GitBranch, List, LayoutGrid, ArrowUp, ArrowDown, ArrowRight,
  ClipboardList, Bell, Timer, MessageSquare, History, Sparkles, Tag,
  SortAsc, User, CalendarDays, Database, RefreshCw, X, LayoutTemplate,
  CheckCircle, Bot, UserPlus, ThumbsUp, Send, Eye, Settings2
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { api } from '../api/client';
import HierarchicalRoadmap from '../components/HierarchicalRoadmap';
import DetailDrawerV2 from '../components/DetailDrawerV2';
import EnhancedFilterBarV2 from '../components/EnhancedFilterBarV2';
import { useTenant } from '../context/TenantContext';
import CockpitQuickActions from '../components/CockpitQuickActions';
import StakeholdersPanel from '../components/StakeholdersPanel';
import AskAnalyticsButton from '../components/AskAnalyticsButton';
import PMAssistantWidget from '../components/PMAssistantWidget';

/**
 * Project Manager Center V2 - Comprehensive corrections applied
 * A) Governance & Stakeholders at top
 * B) Action Items below KPIs
 * C) Detail Drawer for all clickable items
 * D) Filter Bar with Source filter
 * E) Hierarchy with sorting & enhanced display
 * F) Feature-level milestones only
 * G) Severity-based prioritization
 * H) Multi-project visual tagging
 * I) KPI trends
 * J) Sprint/Release badges
 * K) Tighter layout
 */
const ProjectManagerDashboardV2 = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getCurrency, tenantId, tenantConfig } = useTenant();
  
  // Data state
  const [data, setData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [roadmapData, setRoadmapData] = useState([]);
  const [roadmapTimeline, setRoadmapTimeline] = useState(null);  // NEW: Store timeline range
  const [releases, setReleases] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadTime, setLoadTime] = useState(null);
  
  // Filter state with Source filter (D)
  const [filters, setFilters] = useState({
    portfolio_ids: [],
    program_ids: [],
    project_ids: searchParams.get('project_ids')?.split(',').filter(Boolean) || [],
    pm_ids: [],
    sprint_ids: [],
    release_ids: [],
    statuses: [],
    health_categories: [],
    confidence_min: 0,
    confidence_max: 100,
    work_item_types: [],
    sources: [] // D) Source filter: Excel, Manual, DevOps, Jira
  });
  
  // UI state
  const [stakeholdersExpanded, setStakeholdersExpanded] = useState(false);
  const [hierarchyGrouping, setHierarchyGrouping] = useState('epic');
  const [hierarchySorting, setHierarchySorting] = useState('severity'); // E) severity, owner, dueDate
  const [milestoneView, setMilestoneView] = useState('list');
  const [roadmapExpanded, setRoadmapExpanded] = useState(false);
  const [roadmapScale, setRoadmapScale] = useState('sprint');
  
  // Sprint 18: Project Overview toggle state
  const [showProjectOverview, setShowProjectOverview] = useState(false);
  
  // Drawer state (C)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState('');
  const [drawerData, setDrawerData] = useState(null);
  const [drawerTitle, setDrawerTitle] = useState('');

  // Keyboard handler for drawer (C - ESC closes)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && drawerOpen) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen]);

  useEffect(() => {
    const start = performance.now();
    loadInitialData().finally(() => {
      setLoadTime(((performance.now() - start) / 1000).toFixed(2));
    });
  }, []);

  useEffect(() => {
    if (!loading) {
      loadDashboardData();
      if (filters.project_ids.length > 0) {
        setSearchParams({ project_ids: filters.project_ids.join(',') });
      }
    }
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, filtersRes, releasesRes] = await Promise.all([
        api.getProjectManagerDashboardV2(filters),
        api.getFilters(),
        api.getReleases()
      ]);
      
      setData(dashboardRes.data);
      setSprints(filtersRes.data.sprints || []);
      setFilterOptions({
        portfolios: filtersRes.data.portfolios || [],
        programs: filtersRes.data.programs || [],
        projects: dashboardRes.data.project_options || [],
        project_managers: filtersRes.data.project_managers || [],
        sprints: filtersRes.data.sprints || [],
        releases: releasesRes.data.releases || [],
        sources: [
          { id: 'Excel', name: 'Excel' },
          { id: 'Manual', name: 'Manual Entry' },
          { id: 'DevOps', name: 'Azure DevOps' },
          { id: 'Jira', name: 'Jira' }
        ]
      });
      setReleases(releasesRes.data?.releases || []);
      
      if (!filters.project_ids.length && dashboardRes.data.selected_projects?.length) {
        setFilters(prev => ({
          ...prev,
          project_ids: dashboardRes.data.selected_projects.map(p => p.id)
        }));
      }
    } catch (error) {
      console.error('Failed to load PM dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const projectId = filters.project_ids.length === 1 ? filters.project_ids[0] : null;
      const [res, roadmapRes] = await Promise.all([
        api.getProjectManagerDashboardV2(filters),
        projectId 
          ? api.getPMRoadmap({ project_id: projectId })  // NEW: Use PM view-specific endpoint
          : Promise.resolve({ data: { roadmap: [], timeline: null } })
      ]);
      setData(res.data);
      setRoadmapData(roadmapRes.data?.roadmap || []);
      setRoadmapTimeline(roadmapRes.data?.timeline || null);  // Store timeline range
    } catch (error) {
      console.error('Failed to load PM dashboard:', error);
    }
  };

  // Refresh state
  const [refreshing, setRefreshing] = useState(false);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadDashboardData();
    } finally {
      setRefreshing(false);
    }
  };

  // C) Open drawer for ANY clickable item
  const openDrawer = useCallback((type, data, title) => {
    // Create standardized inspector context
    const context = {
      entityType: (type || data?.type || 'work-item').toLowerCase(),
      entityId: data?.id,
      entityName: title || data?.name || data?.title,
      dateRange: (data?.planned_start && data?.planned_end) ? {
        start: data.planned_start,
        end: data.planned_end
      } : null,
      parentChain: {
        portfolioId: data?.portfolio_id,
        programId: data?.program_id,
        projectId: data?.project_id,
        epicId: data?.epic_id
      },
      item: data
    };
    setDrawerType(context.entityType);
    setDrawerData(context);
    setDrawerTitle(title);
    setDrawerOpen(true);
  }, []);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    // Use getCurrencyCode for ISO code (GBP, USD), not getCurrency (symbol)
    const currencyCode = tenantConfig?.currency_code || 'USD';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    } catch {
      // Fallback if currency code is invalid
      return `${getCurrency()}${value.toLocaleString()}`;
    }
  };

  // J) Handle Sprint/Release badge click
  const handleBadgeClick = (type, id) => {
    if (type === 'sprint') {
      setFilters(prev => ({
        ...prev,
        sprint_ids: prev.sprint_ids.includes(id) 
          ? prev.sprint_ids.filter(s => s !== id)
          : [...prev.sprint_ids, id]
      }));
    } else if (type === 'release') {
      setFilters(prev => ({
        ...prev,
        release_ids: prev.release_ids.includes(id)
          ? prev.release_ids.filter(r => r !== id)
          : [...prev.release_ids, id]
      }));
    }
  };

  const kpis = data?.kpis || {};
  const isMultiProject = data?.is_multi_project || false;
  const selectedProjects = data?.selected_projects || [];
  const stakeholders = data?.stakeholders || [];
  const actionItems = data?.action_items || {};
  const hierarchy = data?.hierarchy || [];
  const milestones = data?.milestones || [];
  const risks = data?.risks || [];
  const issues = data?.issues || [];
  const dependencies = data?.dependencies || [];
  const decisions = data?.decisions || [];

  // B) Count total action items (hide empty groups)
  const totalActionItems = useMemo(() => {
    return Object.values(actionItems).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  }, [actionItems]);

  // D) Filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.project_ids.length > 0) count++;
    if (filters.program_ids.length > 0) count++;
    if (filters.sprint_ids.length > 0) count++;
    if (filters.release_ids.length > 0) count++;
    if (filters.statuses.length > 0) count++;
    if (filters.sources.length > 0) count++;
    if (filters.health_categories.length > 0) count++;
    if (filters.confidence_min > 0 || filters.confidence_max < 100) count++;
    return count;
  }, [filters]);

  // Get current sprint/release names for badges (J)
  const currentSprintNames = useMemo(() => {
    return filters.sprint_ids.map(id => {
      const sprint = sprints.find(s => s.id === id);
      return sprint ? { id, name: sprint.name } : null;
    }).filter(Boolean);
  }, [filters.sprint_ids, sprints]);

  const currentReleaseNames = useMemo(() => {
    return filters.release_ids.map(id => {
      const release = releases.find(r => r.id === id);
      return release ? { id, name: release.name } : null;
    }).filter(Boolean);
  }, [filters.release_ids, releases]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
        <p className="text-sm text-[var(--clr-text-muted)]">Loading Project Manager Center...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--clr-surface-page)]" data-testid="pm-dashboard-v2">
      {/* D) Filter Bar with Source filter + filter count */}
      <EnhancedFilterBarV2
        filters={filters}
        options={filterOptions}
        onChange={setFilters}
        dashboardType="pm"
        filterCount={activeFilterCount}
      />

      {/* Dashboard Header with Quick Actions */}
      <div className="px-6 py-4 flex items-center justify-between bg-[var(--clr-surface-card)] border-b border-[var(--clr-border-light)]">
        <div>
          <h1 className="text-xl font-bold text-[var(--clr-text-primary)] flex items-center gap-2">
            <FolderKanban className="w-6 h-6 text-[var(--clr-brand-primary)]" />
            Project Manager Center
          </h1>
          <p className="text-sm text-[var(--clr-text-secondary)]">Comprehensive project health and tracking</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Sprint 18: Project Overview Toggle */}
          <button
            onClick={() => setShowProjectOverview(!showProjectOverview)}
            className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-all ${
              showProjectOverview 
                ? 'bg-[var(--clr-brand-primary)] text-white' 
                : 'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)] hover:bg-[var(--clr-border-light)]'
            }`}
            data-testid="project-overview-toggle"
          >
            <LayoutTemplate className="w-4 h-4" />
            {showProjectOverview ? 'Hide Overview' : 'Project Overview'}
          </button>
          <CockpitQuickActions
            contextType="project"
            contextId={filters.project_ids?.[0]}
            contextName={selectedProjects?.[0]?.name}
            onRefresh={handleRefresh}
            refreshing={refreshing}
          />
          
          {/* Sprint 23: Ask Analytics Button for PMC */}
          <AskAnalyticsButton 
            context="pmc"
            filters={{
              project_id: filters.project_ids?.[0],
              program_id: filters.program_ids?.[0]
            }}
            variant="pill"
            entryPoint="pmc"
          />
        </div>
      </div>

      {/* J) Sprint & Release Badges Header */}
      {(currentSprintNames.length > 0 || currentReleaseNames.length > 0) && (
        <div className="bg-[var(--clr-surface-card)] border-b border-[var(--clr-border-light)] px-6 py-1.5 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[var(--clr-text-muted)]">Active:</span>
          {currentSprintNames.map(s => (
            <button
              key={s.id}
              onClick={() => handleBadgeClick('sprint', s.id)}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--clr-badge-sprint)] text-white rounded-full text-xs hover:opacity-90 transition-opacity"
            >
              <CalendarDays className="w-3 h-3" />
              {s.name}
              <X className="w-3 h-3" />
            </button>
          ))}
          {currentReleaseNames.map(r => (
            <button
              key={r.id}
              onClick={() => handleBadgeClick('release', r.id)}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--clr-badge-release)] text-white rounded-full text-xs hover:opacity-90 transition-opacity"
            >
              <Tag className="w-3 h-3" />
              {r.name}
              <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      {/* Sprint 18: Project Overview - Consolidated Single-Pane View */}
      {showProjectOverview && (
        <ProjectOverviewPanel
          kpis={kpis}
          stakeholders={stakeholders}
          selectedProjects={selectedProjects}
          actionItems={actionItems}
          getCurrency={getCurrency}
          onDeepLink={(path) => navigate(path)}
          onOpenDrawer={openDrawer}
        />
      )}

      {/* A) Governance & Stakeholders - FIRST BLOCK */}
      <StakeholderSection
        stakeholders={stakeholders}
        selectedProjects={selectedProjects}
        isMultiProject={isMultiProject}
        expanded={stakeholdersExpanded}
        onToggle={() => setStakeholdersExpanded(!stakeholdersExpanded)}
      />

      {/* R1-003: PM Assistant Widget - Agentic PM Intelligence Integration */}
      <div className="px-6 py-2">
        <PMAssistantWidget 
          projectIds={filters.project_ids} 
          compact={true}
        />
      </div>

      {/* I) Sticky KPI Row with Trends */}
      <div className="sticky top-[52px] z-20 bg-white border-b border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
        <div className="px-6 py-2">
          <div className="grid grid-cols-6 gap-2">
            <KPITileV2
              label="% Complete"
              value={`${kpis.percent_complete?.toFixed(1) || 0}%`}
              trend={kpis.complete_trend}
              secondaryValue={isMultiProject ? `${selectedProjects.length} projects` : '+2.1% 7d'}
              status={kpis.percent_complete >= 70 ? 'healthy' : kpis.percent_complete >= 40 ? 'warning' : 'critical'}
              icon={<Target className="w-4 h-4" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'completion', items: [] }, 'Progress Overview')}
            />
            <KPITileV2
              label="Blocked"
              value={kpis.blocked_count || 0}
              trend={kpis.blocked_trend}
              secondaryValue={kpis.blocked_trend ? `${kpis.blocked_trend > 0 ? '+' : ''}${kpis.blocked_trend} 7d` : null}
              status="critical"
              icon={<AlertTriangle className="w-4 h-4" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'blocked', items: actionItems.blockers || [] }, 'Blocked Items')}
            />
            <KPITileV2
              label="Behind"
              value={kpis.behind_count || 0}
              trend={kpis.behind_trend}
              status="critical"
              icon={<Clock className="w-4 h-4" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'behind', items: [] }, 'Behind Schedule')}
            />
            <KPITileV2
              label="At Risk"
              value={kpis.at_risk_count || 0}
              trend={kpis.at_risk_trend}
              status="warning"
              icon={<Flag className="w-4 h-4" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'at_risk', items: [] }, 'At Risk Items')}
            />
            <KPITileV2
              label="Est. Cost"
              value={formatCurrency(kpis.est_cost)}
              secondaryValue={`Var: ${formatCurrency(kpis.variance)}`}
              status="neutral"
              icon={<DollarSign className="w-4 h-4" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'cost', items: [] }, 'Cost Summary')}
            />
            <KPITileV2
              label="Confidence"
              value={`${kpis.delivery_confidence || 0}%`}
              trend={kpis.confidence_trend}
              status={kpis.delivery_confidence >= 70 ? 'healthy' : kpis.delivery_confidence >= 50 ? 'warning' : 'critical'}
              icon={<Shield className="w-4 h-4" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'confidence', items: [] }, 'Delivery Confidence')}
            />
          </div>
        </div>
      </div>

      {/* K) Main Content - Tighter spacing (space-y-3 instead of space-y-4) */}
      <div className="px-6 py-3 space-y-3">
        {/* Sprint 18: Agentic PM Controls */}
        <AgenticPMControls
          projectId={filters.project_ids?.[0]}
          projectName={selectedProjects?.[0]?.name}
          onActionComplete={handleRefresh}
        />

        {/* B) Action Items Section - BELOW KPI ROW */}
        <ActionItemsSection
          actionItems={actionItems}
          totalCount={totalActionItems}
          isMultiProject={isMultiProject}
          onItemClick={(item, type) => openDrawer(type, item, item.name || item.task || 'Action Item')}
        />

        {/* K) Two Column Layout: Hierarchy (left) + Milestones (right) */}
        <div className="grid grid-cols-2 gap-3">
          {/* E) Enhanced Hierarchy View with sorting */}
          <HierarchySection
            hierarchy={hierarchy}
            grouping={hierarchyGrouping}
            onGroupingChange={setHierarchyGrouping}
            sorting={hierarchySorting}
            onSortingChange={setHierarchySorting}
            isMultiProject={isMultiProject}
            storyLayerEnabled={tenantConfig?.work_item_hierarchy?.enable_story_layer !== false}
            onItemClick={(item) => openDrawer(item.type || 'item', item, item.name)}
          />

          {/* F) Feature-Level Milestones Only */}
          <MilestonesSection
            milestones={milestones}
            viewMode={milestoneView}
            onViewChange={setMilestoneView}
            isMultiProject={isMultiProject}
            onMilestoneClick={(m) => openDrawer('feature', m, m.name)}
          />
        </div>

        {/* Four Compact Cards */}
        <div className="grid grid-cols-4 gap-2">
          <CompactCard
            title="High Severity Risks"
            items={risks}
            icon={<AlertTriangle className="w-3.5 h-3.5 text-[var(--clr-sev-blocked)]" />}
            colorScheme="red"
            isMultiProject={isMultiProject}
            onItemClick={(item) => openDrawer('risk', item, item.name || item.title)}
            onViewAll={() => navigate('/risks')}
          />
          <CompactCard
            title="Critical Issues"
            items={issues}
            icon={<AlertCircle className="w-3.5 h-3.5 text-orange-500" />}
            colorScheme="orange"
            isMultiProject={isMultiProject}
            onItemClick={(item) => openDrawer('issue', item, item.name || item.title)}
            onViewAll={() => navigate('/issues')}
          />
          <CompactCard
            title="Dependencies"
            items={dependencies}
            icon={<Link2 className="w-3.5 h-3.5 text-purple-500" />}
            colorScheme="purple"
            isMultiProject={isMultiProject}
            onItemClick={(item) => openDrawer('dependency', item, item.name || item.description)}
            onViewAll={() => navigate('/dependencies')}
          />
          <CompactCard
            title="Decisions"
            items={decisions}
            icon={<MessageSquare className="w-3.5 h-3.5 text-blue-500" />}
            colorScheme="blue"
            isMultiProject={isMultiProject}
            onItemClick={(item) => openDrawer('decision', item, item.name || item.title)}
            onViewAll={() => navigate('/decisions')}
          />
        </div>

        {/* Project Roadmap */}
        <RoadmapSection
          data={roadmapData}
          releases={releases}
          expanded={roadmapExpanded}
          onToggle={() => setRoadmapExpanded(!roadmapExpanded)}
          scale={roadmapScale}
          onScaleChange={setRoadmapScale}
          isMultiProject={isMultiProject}
          selectedProjects={selectedProjects}
          currency={getCurrency()}
          timelineRange={roadmapTimeline}  // Pass timeline range
          onItemClick={(item) => openDrawer(item.type?.toLowerCase() || 'item', item, item.name)}
          onOpenFull={() => navigate('/roadmap')}
        />
      </div>

      {/* L) Load Time Indicator */}
      {loadTime && (
        <div className="fixed bottom-4 right-4 text-xs text-[var(--clr-text-muted)] bg-white px-2 py-1 rounded shadow-[var(--shadow-card)]">
          Loaded in {loadTime}s
        </div>
      )}

      {/* C) Detail Drawer V2 - Opens for ALL clickable items */}
      <DetailDrawerV2
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        type={drawerType}
        context={drawerData}  // Pass as context
        data={drawerData?.item || drawerData}
        title={drawerTitle}
        currency={getCurrency()}
        showAuditLog={true}
        isMultiProject={isMultiProject}
      />
    </div>
  );
};

// ==========================================
// I) KPI Tile V2 with Trend Indicators - Modern Color System
// ==========================================
const KPITileV2 = ({ label, value, secondaryValue, trend, status, icon, onClick }) => {
  // Status determines the left border color using CSS tokens
  const getStatusBorderColor = (s) => {
    const map = {
      critical: 'var(--clr-sev-blocked)',
      warning: 'var(--clr-sev-atrisk)',
      healthy: 'var(--clr-sev-healthy)',
      neutral: 'var(--clr-sev-notstarted)',
      info: 'var(--clr-sev-inprogress)'
    };
    return map[s] || map.neutral;
  };

  // I) Trend logic - for negative metrics (blocked/behind), up is bad
  const isNegativeMetric = ['Blocked', 'Behind', 'At Risk'].includes(label);
  const TrendIcon = trend > 0 ? ArrowUp : trend < 0 ? ArrowDown : ArrowRight;
  const getTrendColor = () => {
    if (trend === 0 || trend === undefined || trend === null) return 'text-[var(--clr-text-muted)]';
    if (isNegativeMetric) {
      return trend > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]';
    }
    return trend > 0 ? 'text-[var(--clr-sev-healthy)]' : 'text-[var(--clr-sev-blocked)]';
  };

  return (
    <button
      onClick={onClick}
      className="p-2 rounded-lg bg-[var(--clr-surface-card)] border-l-4 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-md)] transition-all text-left group"
      style={{ borderLeftColor: getStatusBorderColor(status) }}
      data-testid={`kpi-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[var(--clr-text-muted)]">{icon}</span>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-0.5 ${getTrendColor()}`}>
            <TrendIcon className="w-3 h-3" />
            <span className="text-[10px]">{Math.abs(trend)}</span>
          </div>
        )}
      </div>
      <div className="text-lg font-bold text-[var(--clr-text-primary)] group-hover:opacity-80">{value}</div>
      <div className="text-[10px] text-[var(--clr-text-muted)]">{label}</div>
      {secondaryValue && <div className="text-[10px] text-[var(--clr-text-muted)] opacity-70">{secondaryValue}</div>}
    </button>
  );
};

// ==========================================
// Sprint 18: Project Overview Panel - Consolidated Single-Pane View
// ==========================================
const ProjectOverviewPanel = ({ kpis, stakeholders, selectedProjects, actionItems, getCurrency, onDeepLink, onOpenDrawer }) => {
  const formatCurrency = (val) => {
    if (!val && val !== 0) return '-';
    const abs = Math.abs(val);
    if (abs >= 1000000000) return `${getCurrency()}${(val / 1000000000).toFixed(1)}B`;
    if (abs >= 1000000) return `${getCurrency()}${(val / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `${getCurrency()}${(val / 1000).toFixed(0)}K`;
    return `${getCurrency()}${val.toFixed(0)}`;
  };

  // Budget data for chart
  const budgetData = [
    { name: 'Est', value: kpis.est_cost || 0, color: 'var(--clr-sev-inprogress)' },
    { name: 'Actual', value: kpis.act_cost || 0, color: 'var(--clr-sev-healthy)' },
  ];

  // Resource allocation mock data (would come from API)
  const resourceData = [
    { name: 'Allocated', value: 85, color: 'var(--clr-brand-primary)' },
    { name: 'Available', value: 15, color: 'var(--clr-border-light)' },
  ];

  return (
    <div className="bg-[var(--clr-surface-subtle)] border-b border-[var(--clr-border-light)] p-6" data-testid="project-overview-panel">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Commercials/Financials Section */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg p-4 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[var(--clr-brand-primary)]" />
              Financials
            </h3>
            <button 
              onClick={() => onDeepLink('/portfolio/investments')}
              className="text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1"
            >
              Details <ExternalLink className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[var(--clr-text-muted)]">Estimated Cost</span>
              <span className="text-sm font-semibold text-[var(--clr-text-primary)]">{formatCurrency(kpis.est_cost)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[var(--clr-text-muted)]">Actual Cost</span>
              <span className="text-sm font-semibold text-[var(--clr-text-primary)]">{formatCurrency(kpis.act_cost)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[var(--clr-text-muted)]">Variance</span>
              <span className={`text-sm font-semibold ${kpis.variance > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]'}`}>
                {formatCurrency(kpis.variance)}
              </span>
            </div>
            <div className="h-16 mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={budgetData} layout="vertical">
                  <Bar dataKey="value" radius={4}>
                    {budgetData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Resource Allocation Section */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg p-4 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
              <Users className="w-4 h-4 text-[var(--clr-brand-primary)]" />
              Resources
            </h3>
            <button 
              onClick={() => onDeepLink('/resources')}
              className="text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1"
            >
              Details <ExternalLink className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[var(--clr-text-muted)]">Allocation</span>
              <span className="text-sm font-semibold text-[var(--clr-text-primary)]">{kpis?.allocation_pct ?? 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[var(--clr-text-muted)]">Team Size</span>
              <span className="text-sm font-semibold text-[var(--clr-text-primary)]">{stakeholders?.length || 0}</span>
            </div>
            <div className="h-16 mt-2 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={resourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={15}
                    outerRadius={25}
                    dataKey="value"
                  >
                    {resourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Budget Status Section */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg p-4 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
              <Target className="w-4 h-4 text-[var(--clr-brand-primary)]" />
              Budget Status
            </h3>
            <button 
              onClick={() => onDeepLink('/analytics/financial')}
              className="text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1"
            >
              Details <ExternalLink className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-[var(--clr-text-muted)]">Budget Utilization</span>
              <span className="text-sm font-semibold text-[var(--clr-text-primary)]">
                {kpis.est_cost ? Math.round((kpis.act_cost / kpis.est_cost) * 100) : 0}%
              </span>
            </div>
            <div className="w-full bg-[var(--clr-surface-subtle)] rounded-full h-2">
              <div 
                className="h-2 rounded-full transition-all"
                style={{ 
                  width: `${Math.min(100, kpis.est_cost ? (kpis.act_cost / kpis.est_cost) * 100 : 0)}%`,
                  backgroundColor: kpis.act_cost > kpis.est_cost ? 'var(--clr-sev-blocked)' : 'var(--clr-sev-healthy)'
                }}
              />
            </div>
            <div className="flex justify-between items-center pt-2">
              <span className="text-xs text-[var(--clr-text-muted)]">Remaining</span>
              <span className="text-sm font-semibold text-[var(--clr-sev-healthy)]">
                {formatCurrency(Math.max(0, (kpis.est_cost || 0) - (kpis.act_cost || 0)))}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-[var(--clr-text-muted)]">Forecast</span>
              <span className="text-xs text-[var(--clr-text-secondary)]">On track</span>
            </div>
          </div>
        </div>

        {/* Stakeholders Summary Section */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg p-4 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
              <Crown className="w-4 h-4 text-[var(--clr-brand-primary)]" />
              Key Stakeholders
            </h3>
            <button 
              onClick={() => onOpenDrawer('stakeholders', stakeholders, 'Stakeholders')}
              className="text-xs text-[var(--clr-brand-primary)] hover:underline flex items-center gap-1"
            >
              View All <ExternalLink className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            {stakeholders?.slice(0, 4).map((sh, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[var(--clr-surface-subtle)] flex items-center justify-center text-[var(--clr-brand-primary)] font-medium">
                    {sh.name?.charAt(0) || 'U'}
                  </div>
                  <span className="text-[var(--clr-text-primary)] truncate max-w-[100px]">{sh.name}</span>
                </div>
                <span className="px-2 py-0.5 bg-[var(--clr-surface-subtle)] rounded text-[var(--clr-text-muted)]">
                  {sh.role_type || sh.role || 'Member'}
                </span>
              </div>
            ))}
            {(!stakeholders || stakeholders.length === 0) && (
              <p className="text-xs text-[var(--clr-text-muted)] italic">No stakeholders assigned</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// Sprint 18: Agentic PM Controls - AI-Assisted PM Actions
// ==========================================
const AgenticPMControls = ({ projectId, projectName, onActionComplete }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showInstructModal, setShowInstructModal] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const handleAgenticAction = async (action, payload) => {
    setLoading(true);
    setActionResult(null);
    try {
      const response = await api.post(`/agentic/pm/actions`, {
        action,
        project_id: projectId,
        ...payload
      });
      setActionResult({ success: true, message: response.data?.message || 'Action completed' });
      onActionComplete?.();
    } catch (error) {
      setActionResult({ 
        success: false, 
        message: error.response?.data?.detail || 'Action failed' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full flex items-center justify-center gap-2 p-2 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg text-sm text-purple-700 hover:bg-purple-100 transition-colors"
        data-testid="agentic-pm-expand"
      >
        <Bot className="w-4 h-4" />
        <span>Agentic PM Controls</span>
        <Sparkles className="w-3 h-3" />
        <ChevronDown className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4" data-testid="agentic-pm-controls">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
          <Bot className="w-4 h-4" />
          Agentic PM Controls
          <span className="text-xs bg-purple-200 px-2 py-0.5 rounded-full">AI-Assisted</span>
        </h3>
        <button
          onClick={() => setExpanded(false)}
          className="text-purple-600 hover:text-purple-800"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <button
          onClick={() => setShowAssignModal(true)}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-purple-300 rounded-lg text-sm text-purple-700 hover:bg-purple-50 transition-colors disabled:opacity-50"
          data-testid="agentic-assign-btn"
        >
          <UserPlus className="w-4 h-4" />
          Assign
        </button>
        <button
          onClick={() => setShowApproveModal(true)}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-green-300 rounded-lg text-sm text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50"
          data-testid="agentic-approve-btn"
        >
          <ThumbsUp className="w-4 h-4" />
          Approve
        </button>
        <button
          onClick={() => setShowInstructModal(true)}
          disabled={loading}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm text-blue-700 hover:bg-blue-50 transition-colors disabled:opacity-50"
          data-testid="agentic-instruct-btn"
        >
          <Send className="w-4 h-4" />
          Instruct
        </button>
      </div>

      {/* Action Result */}
      {actionResult && (
        <div className={`p-2 rounded-lg text-xs ${
          actionResult.success 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {actionResult.success ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
          {actionResult.message}
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="flex items-center justify-center gap-2 p-2 text-purple-600 text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
          Processing...
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <AgenticModal
          title="Assign Task"
          icon={<UserPlus className="w-5 h-5 text-purple-600" />}
          onClose={() => setShowAssignModal(false)}
          onSubmit={(data) => {
            handleAgenticAction('assign', data);
            setShowAssignModal(false);
          }}
          fields={[
            { name: 'task_id', label: 'Task ID or Name', type: 'text', required: true },
            { name: 'assignee', label: 'Assignee Email', type: 'text', required: true },
            { name: 'notes', label: 'Notes', type: 'textarea' }
          ]}
        />
      )}

      {/* Approve Modal */}
      {showApproveModal && (
        <AgenticModal
          title="Approve Task"
          icon={<ThumbsUp className="w-5 h-5 text-green-600" />}
          onClose={() => setShowApproveModal(false)}
          onSubmit={(data) => {
            handleAgenticAction('approve', data);
            setShowApproveModal(false);
          }}
          fields={[
            { name: 'task_id', label: 'Task ID or Name', type: 'text', required: true },
            { name: 'decision', label: 'Decision', type: 'select', options: ['Approve', 'Reject', 'Request Changes'], required: true },
            { name: 'comments', label: 'Comments', type: 'textarea' }
          ]}
        />
      )}

      {/* Instruct Modal */}
      {showInstructModal && (
        <AgenticModal
          title="Send Instructions"
          icon={<Send className="w-5 h-5 text-blue-600" />}
          onClose={() => setShowInstructModal(false)}
          onSubmit={(data) => {
            handleAgenticAction('instruct', data);
            setShowInstructModal(false);
          }}
          fields={[
            { name: 'recipient', label: 'Recipient (team or individual)', type: 'text', required: true },
            { name: 'instruction', label: 'Instruction', type: 'textarea', required: true },
            { name: 'priority', label: 'Priority', type: 'select', options: ['Normal', 'High', 'Critical'] }
          ]}
        />
      )}

      {/* Audit Trail Link */}
      <div className="mt-3 pt-2 border-t border-purple-200">
        <button 
          className="text-xs text-purple-600 hover:underline flex items-center gap-1"
          onClick={() => window.open(`/governance/audit-trail?entity_type=project&entity_id=${projectId}`, '_blank')}
        >
          <History className="w-3 h-3" />
          View Action Audit Trail
        </button>
      </div>
    </div>
  );
};

// ==========================================
// Agentic Modal Component
// ==========================================
const AgenticModal = ({ title, icon, onClose, onSubmit, fields }) => {
  const [formData, setFormData] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="font-semibold text-[var(--clr-text-primary)]">{title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {fields.map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-[var(--clr-text-secondary)] mb-1">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  className="w-full px-3 py-2 border border-[var(--clr-border-light)] rounded-lg text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
                  rows={3}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  required={field.required}
                />
              ) : field.type === 'select' ? (
                <select
                  className="w-full px-3 py-2 border border-[var(--clr-border-light)] rounded-lg text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  required={field.required}
                >
                  <option value="">Select...</option>
                  {field.options?.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  className="w-full px-3 py-2 border border-[var(--clr-border-light)] rounded-lg text-sm focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  required={field.required}
                />
              )}
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-[var(--clr-text-secondary)] hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==========================================
// A) Stakeholder Section - First Block, Collapsible - Modern Color System
// ==========================================
const StakeholderSection = ({ stakeholders, selectedProjects, isMultiProject, expanded, onToggle }) => {
  const roleIcons = {
    project_manager: <Briefcase className="w-3 h-3" />,
    product_owner: <Users className="w-3 h-3" />,
    sponsor: <Crown className="w-3 h-3" />,
    technical_lead: <GitBranch className="w-3 h-3" />,
    delivery_lead: <Target className="w-3 h-3" />,
    steerco_member: <Users className="w-3 h-3" />
  };

  const roleLabels = {
    project_manager: 'PM',
    product_owner: 'PO',
    sponsor: 'Sponsor',
    technical_lead: 'Tech Lead',
    delivery_lead: 'Delivery',
    steerco_member: 'Steerco'
  };

  // Role colors using semantic tokens
  const roleColors = {
    project_manager: 'bg-[var(--clr-badge-sprint)] text-white',
    product_owner: 'bg-[var(--clr-brand-primary)] text-white',
    sponsor: 'bg-[var(--clr-sev-atrisk)] text-white',
    technical_lead: 'bg-[var(--clr-sev-healthy)] text-white',
    delivery_lead: 'bg-[var(--clr-sev-inprogress)] text-white',
    steerco_member: 'bg-[var(--clr-sev-notstarted)] text-white'
  };

  // Project colors for multi-project badges (H)
  const projectColors = [
    'bg-[var(--clr-badge-sprint)]', 
    'bg-[var(--clr-sev-healthy)]', 
    'bg-[var(--clr-sev-atrisk)]', 
    'bg-[var(--clr-brand-primary)]', 
    'bg-[var(--clr-sev-blocked)]'
  ];
  const getProjectColor = (projectId) => {
    const idx = selectedProjects.findIndex(p => p.id === projectId);
    return projectColors[idx % projectColors.length];
  };

  const groupedByRole = useMemo(() => {
    const groups = {};
    stakeholders.forEach(s => {
      if (!groups[s.role]) groups[s.role] = [];
      groups[s.role].push(s);
    });
    return groups;
  }, [stakeholders]);

  // A) Collapsed: show chips
  const allChips = useMemo(() => {
    return stakeholders.map(s => ({
      ...s,
      label: roleLabels[s.role] || s.role,
      color: roleColors[s.role] || 'bg-[var(--clr-sev-notstarted)] text-white'
    }));
  }, [stakeholders]);

  return (
    <div className="bg-[var(--clr-surface-card)] border-b border-[var(--clr-border-light)]">
      <button
        onClick={onToggle}
        className="w-full px-6 py-2 flex items-center justify-between hover:bg-[var(--clr-surface-hover)] transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[var(--clr-text-muted)]" />
          <span className="font-medium text-[var(--clr-text-secondary)] text-sm">Project Governance & Stakeholders</span>
          {isMultiProject && (
            <span className="text-[10px] bg-[var(--clr-badge-sprint)] text-white px-1.5 py-0.5 rounded-full">
              {selectedProjects.length} projects
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* A) Collapsed: show role chips */}
          {!expanded && allChips.length > 0 && (
            <div className="flex gap-1 flex-wrap max-w-md">
              {allChips.slice(0, 6).map((chip, i) => (
                <div
                  key={i}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${chip.color}`}
                  title={`${chip.name} (${chip.label})`}
                >
                  {roleIcons[chip.role]}
                  <span className="truncate max-w-[60px]">{chip.name?.split(' ')[0]}</span>
                  {/* H) Multi-project badge */}
                  {isMultiProject && chip.project_name && (
                    <span className={`w-1.5 h-1.5 rounded-full ${getProjectColor(chip.project_id)}`} title={chip.project_name} />
                  )}
                </div>
              ))}
              {allChips.length > 6 && (
                <span className="text-[10px] text-[var(--clr-text-muted)]">+{allChips.length - 6}</span>
              )}
            </div>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-[var(--clr-text-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--clr-text-muted)]" />}
        </div>
      </button>

      {/* A) Expanded: full details */}
      {expanded && (
        <div className="px-6 pb-2 pt-1">
          <div className="grid grid-cols-6 gap-2">
            {Object.entries(groupedByRole).map(([role, people]) => (
              <div key={role} className="space-y-1">
                <div className="text-[10px] font-medium text-[var(--clr-text-muted)] flex items-center gap-1">
                  {roleIcons[role]}
                  {roleLabels[role]}
                </div>
                {people.map((person, idx) => (
                  <div key={idx} className="flex items-center gap-1 text-xs">
                    <span className="text-[var(--clr-text-secondary)] truncate">{person.name}</span>
                    {person.email && (
                      <a href={`mailto:${person.email}`} className="text-blue-500 hover:text-[var(--clr-brand-primary)] flex-shrink-0">
                        <Mail className="w-3 h-3" />
                      </a>
                    )}
                    {/* H) Multi-project badge */}
                    {isMultiProject && person.project_name && (
                      <span className={`w-2 h-2 rounded-full ${getProjectColor(person.project_id)} flex-shrink-0`} title={person.project_name} />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// B) Action Items Section - Below KPIs, Grouped - Modern Color System
// ==========================================
const ActionItemsSection = ({ actionItems, totalCount, isMultiProject, onItemClick }) => {
  const [expandedCategory, setExpandedCategory] = useState('blockers');

  // B) All 10 categories with semantic color tokens
  const categories = [
    { key: 'blockers', label: 'Blockers', icon: AlertTriangle, color: 'blocked' },
    { key: 'overdue', label: 'Overdue', icon: Timer, color: 'behind' },
    { key: 'due_7_days', label: 'Due 7 Days', icon: Clock, color: 'atrisk' },
    { key: 'due_14_days', label: 'Due 14 Days', icon: Clock, color: 'atrisk' },
    { key: 'new_risks', label: 'New Risks', icon: AlertTriangle, color: 'blocked' },
    { key: 'new_issues', label: 'New Issues', icon: AlertCircle, color: 'atrisk' },
    { key: 'owned_dependencies', label: 'Dependencies', icon: Link2, color: 'brand' },
    { key: 'pending_decisions', label: 'Decisions', icon: MessageSquare, color: 'inprogress' },
    { key: 'metadata_gaps', label: 'Missing Data', icon: FileText, color: 'notstarted' },
    { key: 'integration_updates', label: 'Sync Updates', icon: RefreshCw, color: 'inprogress' }
  ];

  // Color token mapping
  const getCategoryColors = (color) => {
    const map = {
      blocked: { icon: 'text-[var(--clr-sev-blocked)]', chip: 'bg-[var(--clr-sev-blocked)] text-white' },
      behind: { icon: 'text-[var(--clr-sev-behind)]', chip: 'bg-[var(--clr-sev-behind)] text-white' },
      atrisk: { icon: 'text-[var(--clr-sev-atrisk)]', chip: 'bg-[var(--clr-sev-atrisk)] text-white' },
      brand: { icon: 'text-[var(--clr-brand-primary)]', chip: 'bg-[var(--clr-brand-primary)] text-white' },
      inprogress: { icon: 'text-[var(--clr-sev-inprogress)]', chip: 'bg-[var(--clr-sev-inprogress)] text-white' },
      notstarted: { icon: 'text-[var(--clr-sev-notstarted)]', chip: 'bg-[var(--clr-sev-notstarted)] text-white' }
    };
    return map[color] || map.notstarted;
  };

  // B) Hide empty groups
  const nonEmptyCategories = categories.filter(cat => (actionItems[cat.key]?.length || 0) > 0);

  if (totalCount === 0) {
    return (
      <section className="bg-[var(--clr-surface-card)] rounded-lg border-l-4 p-3 text-center shadow-[var(--shadow-card)]" style={{ borderLeftColor: 'var(--clr-sev-healthy)' }}>
        <span className="text-sm text-[var(--clr-sev-healthy)]">No action items - you're all caught up!</span>
      </section>
    );
  }

  return (
    <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
      <div className="px-3 py-1.5 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-subtle)] flex items-center justify-between">
        <h2 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2 text-sm">
          <Bell className="w-4 h-4 text-[var(--clr-sev-blocked)]" />
          Your Action Items
          <span className="bg-[var(--clr-sev-blocked)] text-white text-[10px] px-1.5 py-0.5 rounded-full">{totalCount}</span>
        </h2>
      </div>

      <div className="flex">
        {/* Category Tabs */}
        <div className="w-40 border-r border-[var(--clr-border-light)] py-1">
          {nonEmptyCategories.map(cat => {
            const count = actionItems[cat.key]?.length || 0;
            const Icon = cat.icon;
            const isActive = expandedCategory === cat.key;
            const colors = getCategoryColors(cat.color);
            
            return (
              <button
                key={cat.key}
                onClick={() => setExpandedCategory(cat.key)}
                className={`w-full px-2 py-1 text-left flex items-center justify-between text-[11px] transition-colors ${
                  isActive ? 'bg-[var(--clr-surface-active)] text-[var(--clr-text-primary)]' : 'text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-hover)]'
                }`}
              >
                <span className="flex items-center gap-1.5 truncate">
                  <Icon className={`w-3 h-3 ${colors.icon}`} />
                  <span className="truncate">{cat.label}</span>
                </span>
                <span className={`px-1 py-0.5 rounded-full text-[9px] font-medium ${colors.chip}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Items List */}
        <div className="flex-1 max-h-56 overflow-y-auto p-2">
          {(actionItems[expandedCategory] || []).length === 0 ? (
            <div className="text-center py-4 text-[var(--clr-text-muted)] text-xs">No items</div>
          ) : (
            <div className="space-y-1">
              {(actionItems[expandedCategory] || []).slice(0, 15).map((item, idx) => (
                <ActionItemRow 
                  key={item.id || idx} 
                  item={item} 
                  category={expandedCategory}
                  isMultiProject={isMultiProject}
                  onClick={() => onItemClick(item, expandedCategory)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// B) Action Item Row with urgency badge and project tag (H) - Modern Color System
const ActionItemRow = ({ item, category, isMultiProject, onClick }) => {
  const getStatusDotColor = (status) => {
    const map = {
      'Blocked': 'bg-[var(--clr-sev-blocked)]',
      'Behind': 'bg-[var(--clr-sev-behind)]',
      'At Risk': 'bg-[var(--clr-sev-atrisk)]',
      'In Progress': 'bg-[var(--clr-sev-inprogress)]',
      'Overdue': 'bg-[var(--clr-sev-behind)]'
    };
    return map[status] || 'bg-[var(--clr-sev-notstarted)]';
  };

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 p-1.5 rounded hover:bg-[var(--clr-surface-hover)] transition-colors text-left group"
    >
      <span className={`w-1 h-6 rounded-full flex-shrink-0 ${getStatusDotColor(item.status)}`} />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[var(--clr-text-secondary)] truncate group-hover:text-[var(--clr-text-primary)]">
          {item.name || item.task || item.title || item.description || 'Unnamed'}
        </div>
        <div className="text-[10px] text-[var(--clr-text-muted)] flex items-center gap-2">
          {item.status && <span>{item.status}</span>}
          {item.days_overdue && <span className="text-[var(--clr-sev-blocked)] font-medium">{item.days_overdue}d late</span>}
          {item.days_until !== undefined && item.days_until >= 0 && <span>Due in {item.days_until}d</span>}
          {item.missing_fields && <span className="text-[var(--clr-sev-atrisk)]">Missing: {item.missing_fields.slice(0, 2).join(', ')}</span>}
        </div>
      </div>
      {/* H) Project tag in multi-project mode */}
      {isMultiProject && item.project_name && (
        <span className="text-[9px] bg-[var(--clr-surface-active)] text-[var(--clr-text-muted)] px-1 py-0.5 rounded flex-shrink-0">
          {item.project_name?.slice(0, 8)}
        </span>
      )}
      <ChevronRight className="w-3 h-3 text-[var(--clr-text-muted)] flex-shrink-0" />
    </button>
  );
};

// ==========================================
// E) Enhanced Hierarchy Section with Sorting - Modern Color System
// ==========================================
const HierarchySection = ({ hierarchy, grouping, onGroupingChange, sorting, onSortingChange, isMultiProject, storyLayerEnabled, onItemClick }) => {
  // Status colors using semantic tokens
  const getStatusDotColor = (status) => {
    const map = {
      'Blocked': 'bg-[var(--clr-sev-blocked)]',
      'Behind': 'bg-[var(--clr-sev-behind)]',
      'At Risk': 'bg-[var(--clr-sev-atrisk)]',
      'In Progress': 'bg-[var(--clr-sev-inprogress)]',
      'Completed': 'bg-[var(--clr-sev-completed)]',
      'Not Started': 'bg-[var(--clr-sev-notstarted)]'
    };
    return map[status] || 'bg-[var(--clr-sev-notstarted)]';
  };

  const getStatusChipColor = (status) => {
    const map = {
      'Blocked': 'bg-[var(--clr-sev-blocked)]',
      'Behind': 'bg-[var(--clr-sev-behind)]',
      'At Risk': 'bg-[var(--clr-sev-atrisk)]',
      'In Progress': 'bg-[var(--clr-sev-inprogress)]',
      'Completed': 'bg-[var(--clr-sev-completed)]',
      'Not Started': 'bg-[var(--clr-sev-notstarted)]'
    };
    return map[status] || 'bg-[var(--clr-sev-notstarted)]';
  };

  // G) Severity-based sorting
  const getSeverityScore = (status) => {
    const scores = { 'Blocked': 0, 'Behind': 1, 'At Risk': 2, 'In Progress': 3, 'Completed': 4, 'Not Started': 5 };
    return scores[status] ?? 99;
  };

  // E) Sort hierarchy based on selected sort option
  const sortedHierarchy = useMemo(() => {
    if (!hierarchy || hierarchy.length === 0) return [];
    
    return hierarchy.map(project => {
      const sortedEpics = [...(project.epics || [])].sort((a, b) => {
        if (sorting === 'severity') {
          return getSeverityScore(a.status) - getSeverityScore(b.status);
        } else if (sorting === 'owner') {
          return (a.owner || '').localeCompare(b.owner || '');
        } else if (sorting === 'dueDate') {
          return new Date(a.due_date || '9999') - new Date(b.due_date || '9999');
        }
        return 0;
      });
      return { ...project, epics: sortedEpics };
    });
  }, [hierarchy, sorting]);

  // K) Auto-collapse empty sections
  const hasContent = sortedHierarchy.some(p => p.epics?.length > 0);

  if (!hasContent) {
    return (
      <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden">
        <div className="px-3 py-2 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-page)] flex items-center gap-2">
          <Layers className="w-4 h-4 text-[var(--clr-brand-primary)]" />
          <span className="font-semibold text-[var(--clr-text-primary)] text-sm">Work Breakdown</span>
        </div>
        <div className="p-4 text-center text-[var(--clr-text-muted)] text-xs">
          <Layers className="w-6 h-6 mx-auto mb-1 opacity-40" />
          No work items match current filters
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden">
      <div className="px-3 py-1.5 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-subtle)] flex items-center justify-between">
        <h2 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2 text-sm">
          <Layers className="w-4 h-4 text-[var(--clr-brand-primary)]" />
          Work Breakdown
        </h2>
        <div className="flex items-center gap-2">
          {/* E) Grouping toggle */}
          <div className="flex items-center gap-0.5">
            {['epic', 'feature', 'flat'].map((mode) => (
              <button
                key={mode}
                onClick={() => onGroupingChange(mode)}
                className={`px-1.5 py-0.5 text-[10px] rounded ${
                  grouping === mode ? 'bg-[var(--clr-brand-primary)] text-white' : 'text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)]'
                }`}
              >
                {mode === 'flat' ? 'Tasks' : mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          {/* E) Sorting toggle */}
          <div className="flex items-center gap-0.5 border-l border-[var(--clr-border-light)] pl-2">
            <SortAsc className="w-3 h-3 text-[var(--clr-text-muted)]" />
            {[
              { id: 'severity', label: 'Severity', icon: AlertTriangle },
              { id: 'owner', label: 'Owner', icon: User },
              { id: 'dueDate', label: 'Due', icon: CalendarDays }
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => onSortingChange(id)}
                className={`px-1.5 py-0.5 text-[10px] rounded ${
                  sorting === id ? 'bg-[var(--clr-surface-active)] text-[var(--clr-text-secondary)]' : 'text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)]'
                }`}
                title={`Sort by ${label}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-h-[350px] overflow-y-auto">
        {sortedHierarchy.map(project => (
          <ProjectHierarchy
            key={project.id}
            project={project}
            grouping={grouping}
            getStatusDotColor={getStatusDotColor}
            getStatusChipColor={getStatusChipColor}
            isMultiProject={isMultiProject}
            storyLayerEnabled={storyLayerEnabled}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    </section>
  );
};

// E) Project Hierarchy with enhanced display - Modern Color System
const ProjectHierarchy = ({ project, grouping, getStatusDotColor, getStatusChipColor, isMultiProject, storyLayerEnabled, onItemClick }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border-b border-[var(--clr-border-light)] last:border-b-0">
      {isMultiProject && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-3 py-1.5 flex items-center gap-2 bg-[var(--clr-surface-subtle)] hover:bg-[var(--clr-surface-hover)] transition-colors"
        >
          <ChevronRight className={`w-3 h-3 text-[var(--clr-text-muted)] transition-transform ${expanded ? 'rotate-90' : ''}`} />
          <FolderKanban className="w-3 h-3 text-[var(--clr-brand-secondary)]" />
          <span className="font-medium text-[var(--clr-text-primary)] text-xs">{project.name}</span>
          <span className="text-[10px] text-[var(--clr-text-muted)]">({project.epics?.length || 0} epics)</span>
        </button>
      )}

      {(expanded || !isMultiProject) && (
        <div className={isMultiProject ? 'pl-5' : ''}>
          {grouping === 'epic' && project.epics?.map(epic => (
            <EpicRow 
              key={epic.id} 
              epic={epic} 
              getStatusDotColor={getStatusDotColor}
              getStatusChipColor={getStatusChipColor}
              storyLayerEnabled={storyLayerEnabled}
              isMultiProject={isMultiProject}
              projectName={project.name}
              onItemClick={onItemClick} 
            />
          ))}
          {grouping === 'feature' && project.epics?.flatMap(e => e.features || []).map(feat => (
            <FeatureRow 
              key={feat.id} 
              feature={feat} 
              getStatusDotColor={getStatusDotColor}
              getStatusChipColor={getStatusChipColor}
              isMultiProject={isMultiProject}
              projectName={project.name}
              onItemClick={onItemClick} 
            />
          ))}
          {grouping === 'flat' && project.epics?.flatMap(e => 
            (e.features || []).flatMap(f => [...(f.stories || []), ...(f.tasks || [])])
          ).map((item, idx) => (
            <TaskRow 
              key={item.id || idx} 
              item={item} 
              getStatusDotColor={getStatusDotColor}
              isMultiProject={isMultiProject}
              projectName={project.name}
              onItemClick={onItemClick} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

// E) Epic Row with enhanced display - Modern Color System
const EpicRow = ({ epic, getStatusDotColor, getStatusChipColor, storyLayerEnabled, isMultiProject, projectName, onItemClick }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div className="flex items-center">
        <button
          onClick={() => setExpanded(!expanded)}
          className="px-2 py-1 text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)]"
        >
          <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        <button
          onClick={() => onItemClick({ ...epic, type: 'epic' })}
          className="flex-1 px-2 py-1.5 flex items-center justify-between hover:bg-[var(--clr-surface-hover)] transition-colors text-left"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusDotColor(epic.status)}`} />
            <span className="text-xs text-[var(--clr-text-secondary)] truncate">{epic.name}</span>
            <span className={`px-1 py-0.5 text-[9px] rounded ${getStatusChipColor(epic.status)} text-white flex-shrink-0`}>
              {epic.status}
            </span>
            {/* H) Project tag */}
            {isMultiProject && (
              <span className="text-[9px] bg-[var(--clr-sev-inprogress)] bg-opacity-10 text-[var(--clr-sev-inprogress)] px-1 py-0.5 rounded flex-shrink-0">{projectName?.slice(0, 6)}</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* E) Owner */}
            {epic.owner && <span className="text-[9px] text-[var(--clr-text-muted)]">{epic.owner?.split(' ')[0]}</span>}
            {/* E) % complete bar */}
            <div className="w-14 h-1 bg-[var(--clr-border-light)] rounded-full overflow-hidden">
              <div className="h-full bg-[var(--clr-brand-secondary)]" style={{ width: `${epic.percent_complete || 0}%` }} />
            </div>
            <span className="text-[10px] text-[var(--clr-text-muted)] w-6">{epic.percent_complete?.toFixed(0) || 0}%</span>
            {/* E) Issue/Dep counts */}
            {(epic.issues_count > 0 || epic.deps_count > 0) && (
              <div className="flex gap-1">
                {epic.issues_count > 0 && <span className="text-[9px] text-[var(--clr-sev-atrisk)]">{epic.issues_count}!</span>}
                {epic.deps_count > 0 && <span className="text-[9px] text-[var(--clr-brand-primary)]">{epic.deps_count}</span>}
              </div>
            )}
          </div>
        </button>
      </div>

      {expanded && epic.features?.map(feat => (
        <div key={feat.id} className="pl-6">
          <FeatureRow 
            feature={feat} 
            getStatusDotColor={getStatusDotColor}
            getStatusChipColor={getStatusChipColor}
            isMultiProject={false}
            onItemClick={onItemClick} 
          />
        </div>
      ))}
    </div>
  );
};

// E) Feature Row - Modern Color System
const FeatureRow = ({ feature, getStatusDotColor, getStatusChipColor, isMultiProject, projectName, onItemClick }) => (
  <button
    onClick={() => onItemClick({ ...feature, type: 'feature' })}
    className="w-full px-3 py-1 flex items-center justify-between hover:bg-[var(--clr-surface-hover)] transition-colors text-left"
  >
    <div className="flex items-center gap-2 min-w-0">
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusDotColor ? getStatusDotColor(feature.status) : 'bg-[var(--clr-sev-notstarted)]'}`} />
      <span className="text-[11px] text-[var(--clr-text-secondary)] truncate">{feature.name}</span>
      {isMultiProject && projectName && (
        <span className="text-[9px] bg-[var(--clr-sev-inprogress)] bg-opacity-10 text-[var(--clr-sev-inprogress)] px-1 py-0.5 rounded flex-shrink-0">{projectName?.slice(0, 6)}</span>
      )}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {feature.owner && <span className="text-[9px] text-[var(--clr-text-muted)]">{feature.owner?.split(' ')[0]}</span>}
      <div className="w-10 h-1 bg-[var(--clr-border-light)] rounded-full overflow-hidden">
        <div className="h-full bg-[var(--clr-brand-secondary)]" style={{ width: `${feature.percent_complete || 0}%` }} />
      </div>
      <span className="text-[9px] text-[var(--clr-text-muted)] w-5">{feature.percent_complete?.toFixed(0) || 0}%</span>
    </div>
  </button>
);

// E) Task Row - Modern Color System
const TaskRow = ({ item, getStatusDotColor, isMultiProject, projectName, onItemClick }) => (
  <button
    onClick={() => onItemClick({ ...item, type: item.type || 'task' })}
    className="w-full px-3 py-0.5 flex items-center gap-2 hover:bg-[var(--clr-surface-hover)] transition-colors text-left"
  >
    <span className={`w-1 h-1 rounded-full flex-shrink-0 ${getStatusDotColor ? getStatusDotColor(item.status) : 'bg-[var(--clr-sev-notstarted)]'}`} />
    <span className="text-[10px] text-[var(--clr-text-muted)] truncate flex-1">{item.name || item.task || item.story}</span>
    {isMultiProject && projectName && (
      <span className="text-[8px] bg-[var(--clr-surface-active)] text-[var(--clr-text-muted)] px-1 rounded flex-shrink-0">{projectName?.slice(0, 4)}</span>
    )}
  </button>
);

// ==========================================
// F) Feature Milestones - FEATURE LEVEL ONLY - Modern Color System
// ==========================================
const MilestonesSection = ({ milestones, viewMode, onViewChange, isMultiProject, onMilestoneClick }) => {
  // Urgency styles using semantic tokens
  const getUrgencyStyle = (urgency) => {
    const styles = {
      'overdue': { 
        bg: 'bg-[var(--clr-surface-card)]', 
        border: 'border-l-[var(--clr-sev-blocked)]', 
        text: 'text-[var(--clr-sev-blocked)]', 
        dot: 'bg-[var(--clr-sev-blocked)]' 
      },
      'due-7': { 
        bg: 'bg-[var(--clr-surface-card)]', 
        border: 'border-l-[var(--clr-sev-atrisk)]', 
        text: 'text-[var(--clr-sev-atrisk)]', 
        dot: 'bg-[var(--clr-sev-atrisk)]' 
      },
      'due-30': { 
        bg: 'bg-[var(--clr-surface-card)]', 
        border: 'border-l-[var(--clr-sev-atrisk)]', 
        text: 'text-[var(--clr-sev-atrisk)]', 
        dot: 'bg-[var(--clr-sev-atrisk)]' 
      },
      'future': { 
        bg: 'bg-[var(--clr-surface-card)]', 
        border: 'border-l-[var(--clr-sev-healthy)]', 
        text: 'text-[var(--clr-sev-healthy)]', 
        dot: 'bg-[var(--clr-sev-healthy)]' 
      }
    };
    return styles[urgency] || styles['future'];
  };

  // F) Filter to feature-level only (exclude tasks)
  const featureMilestones = useMemo(() => {
    return milestones.filter(m => m.type === 'feature' || !m.type);
  }, [milestones]);

  // K) Auto-collapse if empty
  if (featureMilestones.length === 0) {
    return (
      <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden">
        <div className="px-3 py-1.5 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-subtle)] flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[var(--clr-sev-healthy)]" />
          <span className="font-semibold text-[var(--clr-text-primary)] text-sm">Feature Milestones</span>
        </div>
        <div className="p-4 text-center text-[var(--clr-text-muted)] text-xs">
          <Calendar className="w-6 h-6 mx-auto mb-1 opacity-40" />
          No feature milestones
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden">
      <div className="px-3 py-1.5 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-subtle)] flex items-center justify-between">
        <h2 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-[var(--clr-sev-healthy)]" />
          Feature Milestones
          <span className="text-[10px] text-[var(--clr-text-muted)] font-normal">({featureMilestones.length})</span>
        </h2>
        <div className="flex items-center gap-0.5">
          {[
            { id: 'list', icon: List, label: 'List' },
            { id: 'timeline', icon: GitBranch, label: 'Timeline' },
            { id: 'calendar', icon: LayoutGrid, label: 'Calendar' }
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => onViewChange(id)}
              className={`p-1 rounded ${viewMode === id ? 'bg-[var(--clr-brand-primary)] text-white' : 'text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)]'}`}
              title={label}
            >
              <Icon className="w-3 h-3" />
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[350px] overflow-y-auto p-2">
        {viewMode === 'list' ? (
          <MilestoneListView milestones={featureMilestones} getUrgencyStyle={getUrgencyStyle} isMultiProject={isMultiProject} onClick={onMilestoneClick} />
        ) : viewMode === 'timeline' ? (
          <MilestoneTimelineView milestones={featureMilestones} getUrgencyStyle={getUrgencyStyle} isMultiProject={isMultiProject} onClick={onMilestoneClick} />
        ) : (
          <MilestoneCalendarView milestones={featureMilestones} getUrgencyStyle={getUrgencyStyle} onClick={onMilestoneClick} />
        )}
      </div>
    </section>
  );
};

// F) Milestone List View with "Due in X days" - WHITE CARDS WITH LEFT STRIP
const MilestoneListView = ({ milestones, getUrgencyStyle, isMultiProject, onClick }) => (
  <div className="space-y-1.5">
    {milestones.slice(0, 15).map((m, idx) => {
      const style = getUrgencyStyle(m.urgency);
      return (
        <button
          key={m.id || idx}
          onClick={() => onClick(m)}
          className={`w-full flex items-center gap-2 p-2 rounded-lg ${style.bg} border border-[var(--clr-border-light)] border-l-[3px] hover:shadow-[var(--shadow-md)] transition-all text-left`}
          style={{ borderLeftColor: style.dot.includes('blocked') ? 'var(--clr-sev-blocked)' : 
                   style.dot.includes('atrisk') ? 'var(--clr-sev-atrisk)' : 'var(--clr-sev-healthy)' }}
        >
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${style.dot}`} />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-[var(--clr-text-primary)] truncate">{m.name}</div>
            {/* H) Project tag in multi-project */}
            {isMultiProject && m.project_name && (
              <div className="text-[10px] text-[var(--clr-text-muted)] truncate">{m.project_name}</div>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            {/* F) "Due in X days" */}
            <div className={`text-xs font-medium ${style.text}`}>
              {m.days_until !== null && m.days_until !== undefined ? (
                m.days_until < 0 ? `${Math.abs(m.days_until)}d late` :
                m.days_until === 0 ? 'Today' : `Due in ${m.days_until}d`
              ) : '-'}
            </div>
            {m.due_date && <div className="text-[10px] text-[var(--clr-text-muted)]">{new Date(m.due_date).toLocaleDateString()}</div>}
          </div>
          {/* F) Small timeline bar with Today marker */}
          <div className="w-12 h-1 bg-[var(--clr-border-light)] rounded-full overflow-hidden relative flex-shrink-0">
            <div className={`h-full ${style.dot}`} style={{ width: '100%' }} />
            <div className="absolute top-0 left-1/2 w-0.5 h-full bg-[var(--clr-text-primary)] opacity-50" />
          </div>
        </button>
      );
    })}
  </div>
);

// Milestone Timeline View - WHITE CARDS WITH LEFT STRIP
const MilestoneTimelineView = ({ milestones, getUrgencyStyle, isMultiProject, onClick }) => (
  <div className="relative">
    <div className="absolute top-2 left-2 bottom-2 w-0.5 bg-[var(--clr-border-light)]" />
    <div className="space-y-2 pl-6">
      {milestones.slice(0, 12).map((m, idx) => {
        const style = getUrgencyStyle(m.urgency);
        return (
          <button
            key={m.id || idx}
            onClick={() => onClick(m)}
            className="w-full text-left relative"
          >
            <div className={`absolute -left-[18px] w-2.5 h-2.5 rounded-full ${style.dot} border-2 border-white`} />
            <div 
              className={`p-2 rounded-lg ${style.bg} border border-[var(--clr-border-light)] border-l-[3px] hover:shadow-[var(--shadow-md)] transition-all`}
              style={{ borderLeftColor: style.dot.includes('blocked') ? 'var(--clr-sev-blocked)' : 
                       style.dot.includes('atrisk') ? 'var(--clr-sev-atrisk)' : 'var(--clr-sev-healthy)' }}
            >
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[var(--clr-text-primary)] truncate">{m.name}</div>
                  {isMultiProject && m.project_name && (
                    <div className="text-[10px] text-[var(--clr-text-muted)]">{m.project_name}</div>
                  )}
                </div>
                <div className={`text-[10px] font-medium ${style.text}`}>
                  {m.days_until !== null ? (m.days_until < 0 ? `${Math.abs(m.days_until)}d late` : `in ${m.days_until}d`) : '-'}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

// Milestone Calendar View - WHITE CARDS WITH LEFT STRIP
const MilestoneCalendarView = ({ milestones, getUrgencyStyle, onClick }) => {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const days = Array.from({ length: 35 }, (_, i) => {
    const d = new Date(startOfMonth);
    d.setDate(d.getDate() + i - startOfMonth.getDay());
    return d;
  });

  const milestonesMap = {};
  milestones.forEach(m => {
    if (m.due_date) {
      const key = new Date(m.due_date).toDateString();
      if (!milestonesMap[key]) milestonesMap[key] = [];
      milestonesMap[key].push(m);
    }
  });

  return (
    <div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-center text-[10px] text-[var(--clr-text-muted)] font-medium">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day, idx) => {
          const dayMilestones = milestonesMap[day.toDateString()] || [];
          const isToday = day.toDateString() === today.toDateString();
          const isCurrentMonth = day.getMonth() === today.getMonth();
          
          return (
            <div
              key={idx}
              className={`min-h-[50px] p-0.5 rounded text-center ${
                isToday ? 'bg-[var(--clr-brand-primary)] bg-opacity-10 ring-1 ring-[var(--clr-brand-primary)]' : 
                isCurrentMonth ? 'bg-[var(--clr-surface-card)]' : 'bg-[var(--clr-surface-subtle)]'
              }`}
            >
              <div className={`text-[10px] ${isToday ? 'text-[var(--clr-brand-primary)] font-bold' : isCurrentMonth ? 'text-[var(--clr-text-secondary)]' : 'text-[var(--clr-text-muted)]'}`}>
                {day.getDate()}
              </div>
              {dayMilestones.slice(0, 2).map((m, i) => {
                const style = getUrgencyStyle(m.urgency);
                return (
                  <button
                    key={m.id || i}
                    onClick={() => onClick(m)}
                    className={`w-full text-left px-0.5 py-0.5 rounded text-[8px] truncate bg-[var(--clr-surface-card)] border-l-2 ${style.text} hover:opacity-80 mt-0.5`}
                    style={{ borderLeftColor: style.dot.includes('blocked') ? 'var(--clr-sev-blocked)' : 
                             style.dot.includes('atrisk') ? 'var(--clr-sev-atrisk)' : 'var(--clr-sev-healthy)' }}
                  >
                    {m.name}
                  </button>
                );
              })}
              {dayMilestones.length > 2 && (
                <div className="text-[8px] text-[var(--clr-text-muted)]">+{dayMilestones.length - 2}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// Compact Card (H) with Multi-project tags - WHITE CARDS WITH LEFT STRIP
// ==========================================
const CompactCard = ({ title, items, icon, colorScheme, isMultiProject, onItemClick, onViewAll }) => {
  // H) All compact cards use white bg + colored left strip
  const getSchemeColors = (scheme) => ({
    red: { 
      card: 'bg-[var(--clr-surface-card)]',
      borderColor: 'var(--clr-sev-blocked)',
      title: 'text-[var(--clr-sev-blocked)]',
      chip: 'bg-[var(--clr-sev-blocked)] text-white'
    },
    orange: { 
      card: 'bg-[var(--clr-surface-card)]',
      borderColor: 'var(--clr-sev-atrisk)',
      title: 'text-[var(--clr-sev-atrisk)]',
      chip: 'bg-[var(--clr-sev-atrisk)] text-white'
    },
    purple: { 
      card: 'bg-[var(--clr-surface-card)]',
      borderColor: 'var(--clr-brand-primary)',
      title: 'text-[var(--clr-brand-primary)]',
      chip: 'bg-[var(--clr-brand-primary)] text-white'
    },
    blue: { 
      card: 'bg-[var(--clr-surface-card)]',
      borderColor: 'var(--clr-sev-inprogress)',
      title: 'text-[var(--clr-sev-inprogress)]',
      chip: 'bg-[var(--clr-sev-inprogress)] text-white'
    }
  }[scheme] || {
    card: 'bg-[var(--clr-surface-card)]',
    borderColor: 'var(--clr-sev-notstarted)',
    title: 'text-[var(--clr-text-secondary)]',
    chip: 'bg-[var(--clr-sev-notstarted)] text-white'
  });
  
  const colors = getSchemeColors(colorScheme);
  const displayItems = items.slice(0, 3);

  return (
    <div 
      className={`rounded-lg border border-[var(--clr-border-light)] border-l-[3px] p-2 ${colors.card} shadow-[var(--shadow-card)]`}
      style={{ borderLeftColor: colors.borderColor }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <h3 className={`font-medium flex items-center gap-1 text-[11px] ${colors.title}`}>
          {icon}
          {title}
        </h3>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors.chip}`}>{items.length}</span>
      </div>

      <div className="space-y-1">
        {displayItems.length > 0 ? displayItems.map((item, idx) => (
          <button
            key={item.id || idx}
            onClick={() => onItemClick(item)}
            className="w-full text-left p-1 bg-[var(--clr-surface-subtle)] rounded hover:bg-[var(--clr-surface-hover)] transition-all"
          >
            <div className="flex items-center gap-1">
              <div className="text-[10px] font-medium text-[var(--clr-text-primary)] truncate flex-1">
                {item.name || item.title || item.description}
              </div>
              {/* H) Project tag */}
              {isMultiProject && item.project_name && (
                <span className="text-[8px] bg-[var(--clr-surface-active)] text-[var(--clr-text-muted)] px-1 rounded flex-shrink-0">
                  {item.project_name?.slice(0, 4)}
                </span>
              )}
            </div>
            {item.severity && (
              <span className={`text-[9px] px-1 py-0.5 rounded ${
                item.severity === 'Critical' ? 'bg-[var(--clr-sev-blocked)] text-white' : 
                item.severity === 'High' ? 'bg-[var(--clr-sev-atrisk)] text-white' : 
                'bg-[var(--clr-sev-notstarted)] text-white'
              }`}>{item.severity}</span>
            )}
          </button>
        )) : (
          <div className="text-[10px] text-[var(--clr-text-muted)] text-center py-1">None</div>
        )}
      </div>

      {items.length > 3 && (
        <button onClick={onViewAll} className="w-full mt-1.5 text-[10px] text-[var(--clr-brand-primary)] hover:opacity-80 flex items-center justify-center gap-0.5">
          +{items.length - 3} more <ChevronRight className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
};

// ==========================================
// Roadmap Section
// ==========================================
const RoadmapSection = ({ data, releases, expanded, onToggle, scale, onScaleChange, isMultiProject, selectedProjects, currency, onItemClick, onOpenFull, timelineRange }) => (
  <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full px-3 py-1.5 flex items-center justify-between bg-[var(--clr-surface-page)] hover:bg-[var(--clr-surface-hover)] transition-colors"
    >
      <h2 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2 text-sm">
        <Map className="w-4 h-4 text-blue-500" />
        Project Roadmap
        {isMultiProject && <span className="text-[10px] text-[var(--clr-brand-primary)]">({selectedProjects.length} projects)</span>}
      </h2>
      <div className="flex items-center gap-2">
        {expanded && (
          <>
            <select
              value={scale}
              onChange={(e) => onScaleChange(e.target.value)}
              className="text-[10px] border rounded px-1 py-0.5"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="sprint">Sprint</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
            </select>
            <button
              onClick={(e) => { e.stopPropagation(); onOpenFull(); }}
              className="text-[10px] text-[var(--clr-brand-primary)] hover:text-blue-700 flex items-center gap-0.5"
            >
              <ExternalLink className="w-3 h-3" />
              Full
            </button>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-[var(--clr-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>
    </button>

    {expanded && (
      <div className="p-2">
        {data.length > 0 ? (
          <HierarchicalRoadmap
            data={data}
            scale={scale}
            showReleaseBands={true}
            releases={releases}
            currency={currency}
            maxDepth={4}  // PM: Project → Epic → Feature → Task
            timelineRange={timelineRange}  // Pass dynamic timeline
            viewType="pm"  // Identify view type
            onItemClick={onItemClick}
          />
        ) : (
          <div className="py-6 text-center text-[var(--clr-text-muted)]">
            <Map className="w-6 h-6 mx-auto mb-1 opacity-50" />
            <p className="text-xs">Select a single project to view roadmap</p>
          </div>
        )}
      </div>
    )}
  </section>
);

export default ProjectManagerDashboardV2;
